import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorItemProps } from "@/v2-backoffice-client/components/selector"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { describeMergedInto } from "@/v2-backoffice-client/customer/merge/customer.merge.utils"
import { isOwnerOutsideUserTeams } from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { useUserCustomerUpdateMutation } from "@/v2-backoffice-client/user/customer/update/user.customer.update.mutation"

export type UserCustomerUpdateSelectorItemProps = Omit<
  SelectorItemProps,
  "value" | "children" | "onSelect" | "warning"
> & {
  customer: CustomerFragment
  user: UserFragment
  /**
   * The customers (of the user, of the user's teams) whose company HubSpot
   * merged into this one, when any: this is the row the leftovers point
   * at, so it is the expected pick and takes no confirmation.
   */
  mergedFrom?: CustomerFragment[]
  onSelect?(): void
}

/**
 * One candidate owner for a user. A customer that owns one of the user's
 * teams is the expected pick and says which team; a customer that owns
 * none of them is the cross-customer case and takes a confirmation. A
 * customer whose company HubSpot merged away cannot be picked at all: the
 * row says which customer to pick instead, and that survivor is in turn
 * the expected pick.
 */
function UserCustomerUpdateSelectorItem(
  props: UserCustomerUpdateSelectorItemProps,
) {
  const {
    customer,
    user,
    mergedFrom = [],
    onSelect,
    ...selectorItemProps
  } = props

  // The row is marked as the owner only once the write is through, so a
  // failed write (toasted below) never shows a tick it did not earn.
  const [update, { loading, error }] = useUserCustomerUpdateMutation({
    onCompleted(data) {
      // errorPolicy is "all" app-wide: a rejected write resolves with the
      // error and no data instead of throwing.
      if (data?.updateUser) onSelect?.()
    },
  })
  errorToast(error, { title: "Failed to update the user's customer" })

  const userTeamIds = new Set(user.Tenants.map((tenant) => tenant.Id))
  const sharedTeamNames = customer.Tenants.filter((tenant) =>
    userTeamIds.has(tenant.Id),
  ).map((tenant) => tenant.Name)
  const isSurvivor = mergedFrom.length > 0
  const isOutsideTeams = !isSurvivor && isOwnerOutsideUserTeams(user, customer)
  const isLinked = Boolean(customer.ExternalCustomerId)
  const mergedAway = describeMergedInto(customer)

  function describe() {
    if (mergedAway) return mergedAway
    if (!isLinked) {
      return "Not linked to HubSpot — link a company before it can own users"
    }
    if (isSurvivor) {
      const names = mergedFrom.map(
        (leftover) => leftover.Name ?? leftover.ExternalCustomerId ?? "Unnamed",
      )
      return `HubSpot merged ${names.join(" and ")} into this customer — the user belongs here`
    }
    if (sharedTeamNames.length > 0) {
      return `Customer of the user's team${sharedTeamNames.length === 1 ? "" : "s"} ${sharedTeamNames.join(", ")}`
    }
    return customer.ExternalCustomerId
  }

  return (
    <Selector.Item
      isLoading={loading}
      value={String(customer.Id)}
      disabled={!isLinked || Boolean(mergedAway)}
      warning={
        isOutsideTeams
          ? "None of the user's teams belong to this customer."
          : undefined
      }
      confirmContent="Update anyway?"
      onSelect={() => {
        update({ variables: { id: user.Id, customerId: customer.Id } })
      }}
      {...selectorItemProps}
    >
      <Selector.ItemText
        primary={customer.Name ?? "Unnamed"}
        secondary={describe()}
      />
    </Selector.Item>
  )
}

export default UserCustomerUpdateSelectorItem
